import { Document, DocumentError } from './document.ts';
import type { NodeData, NodeId, ParagraphFormat, NodeProps, NodeType, Op, Rect, Transaction, Vec2 } from './types.ts';
import { makeNode, isContainer, isFrameLike } from './defaults.ts';
import { computeLayout, isAutoLayout, mindChildren, mindSubtree, mindRootOf, mindLayoutOps } from './layout.ts';
import { syncInstances, instantiate, overrideKeysFor, resolveComponentProps } from './components.ts';
import { resolveTokens, detachFor, BINDABLE, bindProblem } from './tokens.ts';
import { adjustRuns, adjustParagraphs, paragraphAt, paragraphFormats, type ResolvedParagraph } from './text.ts';
import { pathDistanceAt } from './textpath.ts';
import { readySnapshot } from './devchanges.ts';
import { mirrorOps } from './multiEdit.ts';
import type { VariableType, VariableValue } from './types.ts';
import { indexBetween, indicesBetween, newId, parseColor as hex } from './util.ts';
import { apply, invert, localTransform, mul, transformRect, unionRect, rectCenter, nodeOutline, pathToSvg } from './geometry.ts';

export interface EditorOptions {
  clientId?: string;
  /** Called for every locally originated transaction (send these to peers). */
  onTransaction?: (tx: Transaction) => void;
}

interface HistoryEntry {
  label: string;
  undo: Op[];
  redo: Op[];
  selectionBefore: NodeId[];
  selectionAfter: NodeId[];
  pageId: NodeId;
}

export type EditorEvent =
  | { type: 'selection'; selection: NodeId[] }
  | { type: 'page'; pageId: NodeId }
  | { type: 'history' };

export interface ClipboardData {
  format: 'vellum/clipboard';
  nodes: NodeData[];
  roots: NodeId[];
  /** World transforms of roots at copy time (to paste in place). */
  rootWorld: Record<NodeId, { x: number; y: number; rotation: number }>;
  bounds: Rect;
}

/** Undoable editing session over a Document. All UI tools and AI agents go through this. */
export class Editor {
  readonly doc: Document;
  readonly clientId: string;
  selection: NodeId[] = [];
  pageId: NodeId;
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private listeners = new Set<(e: EditorEvent) => void>();
  private onTransaction?: (tx: Transaction) => void;

  // active transaction state
  private txDepth = 0;
  private txOps: Op[] = [];
  private txInverse: Op[] = [];
  private txLabel = '';
  private txChanged = new Set<NodeId>();
  private txKeys = new Set<string>();
  private txSelectionBefore: NodeId[] = [];
  private txNoHistory = false;
  private gesture: { label: string; entry: HistoryEntry | null } | null = null;
  /**
   * Multi-edit: similar layers edited together. Edits inside one of these roots are repeated in
   * the others at the same place (see mirrorOps). null when off.
   */
  multiEdit: NodeId[] | null = null;
  private finalizing = false;

  constructor(doc: Document = Document.create(), opts: EditorOptions = {}) {
    this.doc = doc;
    this.clientId = opts.clientId ?? newId();
    this.onTransaction = opts.onTransaction;
    const first = doc.pages()[0];
    this.pageId = first ? first.id : doc.rootId;
    doc.on((ev) => {
      if (ev.deleted.size && this.selection.some((id) => ev.deleted.has(id))) {
        this.setSelection(this.selection.filter((id) => !ev.deleted.has(id)));
      }
      if (ev.deleted.has(this.pageId)) {
        const p = this.doc.pages()[0];
        if (p) this.setPage(p.id);
      }
    });
  }

  setTransactionHandler(fn: ((tx: Transaction) => void) | undefined) {
    this.onTransaction = fn;
  }

  on(fn: (e: EditorEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: EditorEvent) {
    for (const l of this.listeners) l(e);
  }

  get page(): NodeData {
    return this.doc.must(this.pageId);
  }

  // -------------------------------------------------------------------------
  // Transactions & history
  // -------------------------------------------------------------------------

  /** View-only files: transactions are refused (onBlocked is told) instead of applied. */
  readOnly = false;
  onBlocked?: (label: string) => void;

  /** Run fn as a single undoable, replicated transaction. */
  transact<T>(label: string, fn: () => T, opts: { history?: boolean } = {}): T {
    if (this.txDepth > 0) return fn();
    if (this.readOnly) {
      this.onBlocked?.(label);
      throw new DocumentError('This file is view-only');
    }
    this.txDepth = 1;
    this.txOps = [];
    this.txInverse = [];
    this.txLabel = label;
    this.txChanged = new Set();
    this.txKeys = new Set();
    this.txSelectionBefore = [...this.selection];
    this.txNoHistory = opts.history === false;
    try {
      return this.doc.batch(() => {
        let result: T;
        try {
          result = fn();
        } catch (e) {
          // roll back everything applied in this transaction
          if (this.txInverse.length) this.doc.apply({ ops: this.txInverse, origin: this.clientId });
          this.txOps = [];
          throw e;
        }
        this.finalize();
        return result;
      });
    } finally {
      this.txDepth = 0;
    }
  }

  /** Apply raw ops inside the current transaction (or as a new one). */
  applyOps(ops: Op[], label = 'Edit'): void {
    if (!ops.length) return;
    if (this.txDepth === 0) return this.transact(label, () => this.applyOps(ops));
    // derived ops (layout, instances, tokens) are never mirrored: each root derives its own
    if (this.multiEdit && !this.finalizing) ops = [...ops, ...mirrorOps(this.doc, this.multiEdit, ops)];
    for (const op of ops) {
      if (op.op === 'create') {
        this.txChanged.add(op.node.id);
        if (op.node.parent) this.txChanged.add(op.node.parent);
        for (const k of Object.keys(op.node)) this.txKeys.add(k);
      } else if (op.op === 'delete') {
        const n = this.doc.get(op.id);
        if (n?.parent) this.txChanged.add(n.parent);
        this.txKeys.add('parent');
      } else {
        this.txChanged.add(op.id);
        const n = this.doc.get(op.id);
        if (op.props.parent !== undefined && n?.parent) this.txChanged.add(n.parent);
        if (op.props.parent) this.txChanged.add(op.props.parent);
        for (const k of Object.keys(op.props)) this.txKeys.add(k);
      }
    }
    const inv = this.doc.apply({ ops, origin: this.clientId, label: this.txLabel });
    this.txInverse = [...inv, ...this.txInverse];
    this.txOps.push(...ops);
  }

  private finalize() {
    this.finalizing = true;
    try {
      this.derive();
    } finally {
      this.finalizing = false;
    }
    this.commit();
  }

  private derive() {
    // Derived state: component instances, then layout. Iterate to a fixed point (bounded).
    let changed = new Set([...this.txChanged].filter((id) => this.doc.has(id)));
    for (let pass = 0; pass < 4 && changed.size; pass++) {
      const before = new Set(this.txChanged);
      const tokenOps = resolveTokens(this.doc, changed);
      if (tokenOps.length) this.applyOps(tokenOps);
      const syncOps = syncInstances(this.doc, new Set([...changed, ...tokenOps.map((o) => (o.op === 'set' ? o.id : ''))].filter(Boolean)));
      if (syncOps.length) this.applyOps(syncOps);
      const propOps = resolveComponentProps(this.doc, new Set([...changed, ...syncOps.map((o) => (o.op === 'create' ? o.node.id : o.id))]));
      if (propOps.length) this.applyOps(propOps);
      const layoutOps = computeLayout(this.doc, new Set([...changed, ...this.txChanged]), pass === 0 ? this.txKeys : undefined);
      if (layoutOps.length) this.applyOps(layoutOps);
      changed = new Set([...this.txChanged].filter((id) => !before.has(id) && this.doc.has(id)));
      if (!syncOps.length && !layoutOps.length && !tokenOps.length && !propOps.length) break;
    }
    // Ready for dev: keep a snapshot to compare later changes against
    if (this.txKeys.has('devStatus')) {
      for (const id of this.txChanged) {
        const n = this.doc.get(id);
        if (!n) continue;
        if (n.devStatus === 'READY' && !n.devSnapshot) this.applyOps([{ op: 'set', id, props: { devSnapshot: { at: Date.now(), nodes: readySnapshot(this.doc, id) } } }]);
        if (!n.devStatus && n.devSnapshot) this.applyOps([{ op: 'set', id, props: { devSnapshot: undefined } }]);
      }
    }
    // Remove groups emptied by this transaction.
    const empty = [...this.txChanged].filter((id) => {
      const n = this.doc.get(id);
      return n?.type === 'GROUP' && this.doc.children(id).length === 0;
    });
    if (empty.length) this.applyOps(empty.map((id) => ({ op: 'delete', id })));
  }

  private commit() {
    if (!this.txOps.length) return;
    const entry: HistoryEntry = {
      label: this.txLabel,
      undo: this.txInverse,
      redo: this.txOps,
      selectionBefore: this.txSelectionBefore,
      selectionAfter: [...this.selection],
      pageId: this.pageId,
    };
    if (!this.txNoHistory) {
      if (this.gesture) {
        if (this.gesture.entry) {
          this.gesture.entry.undo = [...entry.undo, ...this.gesture.entry.undo];
          this.gesture.entry.redo = [...this.gesture.entry.redo, ...entry.redo];
          this.gesture.entry.selectionAfter = entry.selectionAfter;
        } else {
          entry.label = this.gesture.label;
          this.gesture.entry = entry;
          this.undoStack.push(entry);
        }
      } else {
        this.undoStack.push(entry);
        if (this.undoStack.length > 500) this.undoStack.shift();
      }
      this.redoStack = [];
      this.emit({ type: 'history' });
    }
    this.onTransaction?.({ ops: this.txOps, origin: this.clientId, label: this.txLabel });
  }

  /** Merge all transactions until endGesture() into one undo step (e.g. a drag). With `continues`,
   *  the step that just created that layer is extended instead (typing into a new text is part of
   *  adding it, so one undo removes both). */
  beginGesture(label: string, opts: { continues?: NodeId } = {}) {
    const last = this.undoStack.at(-1);
    const created = !!opts.continues && !!last && last.pageId === this.pageId && last.redo.some((o) => o.op === 'create' && o.node.id === opts.continues);
    this.gesture = { label, entry: created ? last! : null };
  }
  endGesture() {
    const e = this.gesture?.entry;
    this.gesture = null;
    // a step that added layers and removed them again (a new text left empty) leaves nothing to undo
    if (e && this.undoStack.at(-1) === e && addsNothing(e.redo)) {
      this.undoStack.pop();
      this.emit({ type: 'history' });
    }
  }

  canUndo() {
    return this.undoStack.length > 0;
  }
  canRedo() {
    return this.redoStack.length > 0;
  }

  undo(): boolean {
    const e = this.undoStack.pop();
    if (!e) return false;
    // undoing "add this layer" leaves it if others have since put work inside it (deleting it would delete theirs)
    const added = new Set(e.redo.flatMap((o) => (o.op === 'create' ? [o.node.id] : [])));
    const ops = e.undo.filter((o) => o.op !== 'delete' || !this.doc.has(o.id) || this.doc.subtree(o.id).every((n) => added.has(n.id)));
    const applied = this.applyBestEffort(ops, `Undo ${e.label}`);
    this.redoStack.push({ ...e, undo: applied.inverse, redo: applied.ops.length ? e.redo : [] });
    if (this.doc.has(e.pageId) && e.pageId !== this.pageId) this.setPage(e.pageId);
    this.setSelection(e.selectionBefore.filter((id) => this.doc.has(id)));
    this.emit({ type: 'history' });
    return true;
  }

  redo(): boolean {
    const e = this.redoStack.pop();
    if (!e) return false;
    const applied = this.applyBestEffort(e.redo, `Redo ${e.label}`);
    this.undoStack.push({ ...e, undo: applied.inverse });
    this.setSelection(e.selectionAfter.filter((id) => this.doc.has(id)));
    this.emit({ type: 'history' });
    return true;
  }

  /** Apply ops one by one, skipping ones invalidated by concurrent remote edits. */
  private applyBestEffort(ops: Op[], label: string): { ops: Op[]; inverse: Op[] } {
    const done: Op[] = [];
    let inverse: Op[] = [];
    this.doc.batch(() => {
      for (const op of ops) {
        try {
          const inv = this.doc.apply({ ops: [op], origin: this.clientId, label });
          inverse = [...inv, ...inverse];
          done.push(op);
        } catch (err) {
          if (!(err instanceof DocumentError)) throw err;
        }
      }
    });
    if (done.length) this.onTransaction?.({ ops: done, origin: this.clientId, label });
    return { ops: done, inverse };
  }

  /** Apply a transaction that came from a peer. Not recorded in local history. */
  applyRemote(tx: Transaction) {
    this.doc.apply(tx, { remote: true });
  }

  // -------------------------------------------------------------------------
  // Selection & pages
  // -------------------------------------------------------------------------

  setSelection(ids: NodeId[]) {
    const next = [...new Set(ids)].filter((id) => this.doc.has(id));
    if (next.length === this.selection.length && next.every((id, i) => id === this.selection[i])) return;
    this.selection = next;
    this.emit({ type: 'selection', selection: next });
  }

  selectedNodes(): NodeData[] {
    return this.selection.map((id) => this.doc.get(id)).filter((n): n is NodeData => !!n);
  }

  setPage(pageId: NodeId) {
    const p = this.doc.must(pageId);
    if (p.type !== 'PAGE') throw new Error('Not a page');
    if (this.pageId === pageId) return;
    this.pageId = pageId;
    this.setSelection([]);
    this.emit({ type: 'page', pageId });
  }

  addPage(name = `Page ${this.doc.pages().length + 1}`, kind: 'DESIGN' | 'WHITEBOARD' = 'DESIGN'): NodeId {
    const id = newId();
    this.transact('Add page', () => {
      this.applyOps([
        {
          op: 'create',
          node: makeNode('PAGE', {
            id,
            name,
            parent: this.doc.rootId,
            index: this.doc.indexAt(this.doc.rootId),
            pageKind: kind,
            backgroundColor: kind === 'WHITEBOARD' ? { r: 0.96, g: 0.96, b: 0.96, a: 1 } : undefined,
          }),
        },
      ]);
    });
    return id;
  }

  // -------------------------------------------------------------------------
  // Node commands
  // -------------------------------------------------------------------------

  /** Create a node. props.parent defaults to the current page; position = z-order slot (default: top). */
  create(type: NodeType, props: NodeProps & { id?: string } = {}, position?: number): NodeId {
    const parent = props.parent ?? this.pageId;
    const p = this.doc.must(parent);
    if (!isContainer(p.type)) throw new DocumentError(`${p.type} ${parent} cannot contain children`);
    const node = makeNode(type, { ...props, parent, index: props.index ?? this.doc.indexAt(parent, position) });
    this.transact(`Create ${type.toLowerCase()}`, () => this.applyOps([{ op: 'create', node }]));
    return node.id;
  }

  update(ids: NodeId | NodeId[], props: NodeProps, label = 'Edit properties') {
    const list = Array.isArray(ids) ? ids : [ids];
    this.transact(label, () => {
      const ops: Op[] = [];
      for (const id of list) {
        this.doc.must(id);
        const p: NodeProps = { ...props };
        const ov = overrideKeysFor(this.doc, id, props);
        if (ov) p.overrides = ov;
        Object.assign(p, detachFor(this.doc.must(id), props));
        const cur = this.doc.must(id);
        if (props.characters !== undefined && props.textRuns === undefined && cur.textRuns?.length) {
          const runs = adjustRuns(cur.characters ?? '', props.characters, cur.textRuns);
          p.textRuns = runs.length ? runs : undefined;
        }
        if (props.characters !== undefined && props.paragraphs === undefined && cur.paragraphs?.length) {
          const paras = adjustParagraphs(cur.characters ?? '', props.characters, cur.paragraphs);
          p.paragraphs = paras.length ? paras : undefined;
        }
        // Text layers are named after their content until renamed explicitly.
        const n = this.doc.must(id);
        if (props.characters !== undefined && props.name === undefined && n.type === 'TEXT' && (n.name === 'Text' || n.name === autoName(n.characters ?? ''))) {
          p.name = autoName(props.characters) || 'Text';
        }
        ops.push({ op: 'set', id, props: p });
      }
      this.applyOps(ops);
    });
  }

  /** Remove nodes (and their subtrees). */
  remove(ids: NodeId[]) {
    // a mind map topic takes its branches and their connectors with it
    if (ids.some((id) => this.doc.get(id)?.mindParent || this.doc.get(id)?.mindMap)) {
      const all = new Set(ids.flatMap((id) => (this.doc.get(id)?.mindParent || this.doc.get(id)?.mindMap ? mindSubtree(this.doc, id) : [id])));
      const trees = new Set(ids.map((id) => mindRootOf(this.doc, id)).filter((r): r is NodeId => !!r && !all.has(r)));
      for (const id of [...all]) {
        const n = this.doc.get(id);
        if (!n?.parent || (!n.mindParent && !n.mindMap)) continue;
        for (const c of this.doc.children(n.parent)) if (c.type === 'CONNECTOR' && (c.connectorStart?.nodeId === id || c.connectorEnd?.nodeId === id)) all.add(c.id);
      }
      this.transact('Delete topics', () => {
        this.removeNodes([...all]);
        const ops = mindLayoutOps(this.doc, [...trees]);
        if (ops.length) this.applyOps(ops);
      });
      return;
    }
    this.removeNodes(ids);
  }

  private removeNodes(ids: NodeId[]) {
    const roots = this.topLevel(ids).filter((n) => n.type !== 'DOCUMENT');
    if (!roots.length) return;
    if (roots.some((n) => n.type === 'PAGE') && this.doc.pages().length <= roots.filter((n) => n.type === 'PAGE').length) {
      throw new DocumentError('Cannot delete the last page');
    }
    this.transact(`Delete ${roots.length} layer${roots.length > 1 ? 's' : ''}`, () => {
      this.applyOps(roots.map((n) => ({ op: 'delete', id: n.id })));
      this.setSelection(this.selection.filter((id) => this.doc.has(id)));
    });
  }

  // Mind maps ----------------------------------------------------------------

  /** Start a mind map: a central topic at (x, y). Branches are added with addMindTopic. */
  createMindMap(x: number, y: number, text = 'Central topic', opts: { parent?: NodeId; layout?: 'RIGHT' | 'BOTH' } = {}): NodeId {
    return this.create('SHAPE_WITH_TEXT', {
      name: 'Central topic',
      parent: opts.parent ?? this.pageId,
      x,
      y,
      width: 160,
      height: 48,
      shape: 'ROUNDED_RECTANGLE',
      characters: text,
      fontSize: 20,
      fontWeight: 600,
      fills: [{ type: 'SOLID', color: hex('#1e1e1e') }],
      strokes: [],
      textColor: hex('#ffffff'),
      mindMap: { layout: opts.layout ?? 'RIGHT' },
    });
  }

  /** Add a branch to `topic` (after the branch `after`, else last). Returns the new topic. */
  addMindTopic(topic: NodeId, text = '', after?: NodeId): NodeId {
    const parent = this.doc.must(topic);
    if (!parent.mindParent && !parent.mindMap) throw new DocumentError(`${parent.name} is not a mind map topic`);
    const container = parent.parent!;
    const siblings = mindChildren(this.doc, topic);
    const level1 = parent.mindMap ? null : this.mindBranchRoot(topic);
    const color = MIND_BRANCHES[(level1 ? mindChildren(this.doc, this.doc.must(level1).mindParent!).findIndex((c) => c.id === level1) : siblings.length) % MIND_BRANCHES.length];
    const prev = after && siblings.some((s) => s.id === after) ? this.doc.must(after) : siblings.at(-1) ?? parent;
    const next = this.doc.children(container).find((c) => c.index > prev.index);
    let id = '';
    this.transact('Add topic', () => {
      id = this.create('SHAPE_WITH_TEXT', {
        name: 'Topic',
        parent: container,
        index: indexBetween(prev.index, next?.index ?? null),
        x: parent.x + parent.width + 56,
        y: parent.y,
        width: 120,
        height: 40,
        shape: 'ROUNDED_RECTANGLE',
        characters: text,
        fontSize: parent.mindMap ? 16 : 14,
        fills: [{ type: 'SOLID', color: hex(color.fill) }],
        strokes: [],
        textColor: hex('#1e1e1e'),
        mindParent: topic,
      });
      const bottom = this.doc.children(container)[0];
      this.create('CONNECTOR', {
        parent: container,
        index: indexBetween(null, bottom?.index ?? null),
        connectorType: 'CURVED',
        connectorStart: { nodeId: topic, magnet: 'AUTO', x: 0, y: 0 },
        connectorEnd: { nodeId: id, magnet: 'AUTO', x: 0, y: 0 },
        strokes: [{ type: 'SOLID', color: hex(color.line) }],
        strokeWeight: 2,
        endCap: 'NONE',
      });
    });
    return id;
  }

  /** A topic next to `id` (for the central topic: a new branch). */
  addMindSibling(id: NodeId, text = ''): NodeId {
    const n = this.doc.must(id);
    return n.mindMap ? this.addMindTopic(id, text) : this.addMindTopic(n.mindParent!, text, id);
  }

  /** The first-level branch a topic belongs to (it decides the branch color). */
  private mindBranchRoot(id: NodeId): NodeId {
    let n = this.doc.must(id);
    while (n.mindParent && !this.doc.get(n.mindParent)?.mindMap) n = this.doc.must(n.mindParent);
    return n.id;
  }

  /** Filter a list of ids down to nodes that are not descendants of other listed nodes. */
  topLevel(ids: NodeId[]): NodeData[] {
    const set = new Set(ids);
    return ids
      .map((id) => this.doc.get(id))
      .filter((n): n is NodeData => !!n && !this.doc.ancestors(n.id).some((a) => set.has(a.id)));
  }

  /** Move nodes by a page-space delta. */
  moveBy(ids: NodeId[], dx: number, dy: number, label = 'Move') {
    if (!dx && !dy) return;
    const nodes = this.topLevel(ids).filter((n) => !n.locked && n.type !== 'PAGE');
    this.transact(label, () => {
      const ops: Op[] = [];
      for (const n of nodes) {
        const parent = this.doc.get(n.parent);
        if (isAutoLayout(parent) && n.layoutPositioning !== 'ABSOLUTE') continue;
        if (n.type === 'CONNECTOR') {
          const s = n.connectorStart!,
            e = n.connectorEnd!;
          ops.push({
            op: 'set',
            id: n.id,
            props: {
              connectorStart: s.nodeId ? s : { ...s, x: s.x + dx, y: s.y + dy },
              connectorEnd: e.nodeId ? e : { ...e, x: e.x + dx, y: e.y + dy },
            },
          });
          continue;
        }
        const d = this.deltaToParent(n, dx, dy);
        ops.push({ op: 'set', id: n.id, props: { x: n.x + d.x, y: n.y + d.y } });
      }
      this.applyOps(ops);
    });
  }

  private deltaToParent(n: NodeData, dx: number, dy: number): Vec2 {
    if (!n.parent) return { x: dx, y: dy };
    const m = invert(this.doc.worldTransform(n.parent));
    return { x: m[0] * dx + m[2] * dy, y: m[1] * dx + m[3] * dy };
  }

  /** Set a node's top-left (unrotated box) in page coordinates. */
  setPagePosition(id: NodeId, x: number, y: number) {
    const n = this.doc.must(id);
    const b = this.doc.worldBounds(id);
    this.moveBy([id], x - b.x, y - b.y);
    void n;
  }

  /**
   * Resize a node to w×h. Children of non-auto-layout frames follow their constraints.
   * anchor: which point stays fixed, in normalized node coords (default top-left).
   */
  resize(id: NodeId, width: number, height: number, anchor: Vec2 = { x: 0, y: 0 }, label = 'Resize') {
    const n = this.doc.must(id);
    width = Math.max(n.type === 'LINE' ? 0 : 0.01, width);
    height = Math.max(n.type === 'LINE' ? 0 : 0.01, height);
    this.transact(label, () => {
      const ops: Op[] = [];
      // Keep the anchor point fixed in parent space.
      const before = apply(localTransform(n), { x: anchor.x * n.width, y: anchor.y * n.height });
      const next = { ...n, width, height };
      const after = apply(localTransform(next), { x: anchor.x * width, y: anchor.y * height });
      const props: NodeProps = { width, height, x: n.x + before.x - after.x, y: n.y + before.y - after.y };
      if (n.type === 'TEXT' && n.textAutoResize === 'WIDTH_AND_HEIGHT') props.textAutoResize = 'HEIGHT';
      if (n.type === 'TEXT' && n.textAutoResize === 'HEIGHT' && Math.abs(height - n.height) > 0.5) props.textAutoResize = 'NONE';
      if (isAutoLayout(n)) {
        if (Math.abs(width - n.width) > 0.01 && n.layoutSizingH === 'HUG') props.layoutSizingH = 'FIXED';
        if (Math.abs(height - n.height) > 0.01 && n.layoutSizingV === 'HUG') props.layoutSizingV = 'FIXED';
      }
      const ov = overrideKeysFor(this.doc, id, props);
      if (ov) props.overrides = ov;
      ops.push({ op: 'set', id, props });
      if ((isFrameLike(n.type) || n.type === 'SECTION') && !isAutoLayout(n)) ops.push(...this.constraintOps(n, width, height));
      if (n.type === 'GROUP') ops.push(...this.scaleChildrenOps(n, width / (n.width || 1), height / (n.height || 1)));
      this.applyOps(ops);
    });
  }

  private constraintOps(n: NodeData, w: number, h: number): Op[] {
    const ops: Op[] = [];
    const dw = w - n.width,
      dh = h - n.height;
    for (const c of this.doc.children(n.id)) {
      const props: NodeProps = {};
      const ch = c.constraintH ?? 'MIN';
      const cv = c.constraintV ?? 'MIN';
      if (ch === 'MAX') props.x = c.x + dw;
      else if (ch === 'CENTER') props.x = c.x + dw / 2;
      else if (ch === 'STRETCH') props.width = Math.max(0.01, c.width + dw);
      else if (ch === 'SCALE') {
        const s = w / (n.width || 1);
        props.x = c.x * s;
        props.width = c.width * s;
      }
      if (cv === 'MAX') props.y = c.y + dh;
      else if (cv === 'CENTER') props.y = c.y + dh / 2;
      else if (cv === 'STRETCH') props.height = Math.max(0.01, c.height + dh);
      else if (cv === 'SCALE') {
        const s = h / (n.height || 1);
        props.y = c.y * s;
        props.height = c.height * s;
      }
      if (Object.keys(props).length) ops.push({ op: 'set', id: c.id, props });
    }
    return ops;
  }

  private scaleChildrenOps(g: NodeData, sx: number, sy: number): Op[] {
    const ops: Op[] = [];
    for (const c of this.doc.children(g.id)) {
      ops.push({ op: 'set', id: c.id, props: { x: c.x * sx, y: c.y * sy, width: Math.max(0.01, c.width * sx), height: c.height * sy } });
      if (c.type === 'GROUP') ops.push(...this.scaleChildrenOps(c, sx, sy));
    }
    return ops;
  }

  rotate(id: NodeId, degrees: number) {
    this.update(id, { rotation: normalizeAngle(degrees) }, 'Rotate');
  }

  /** Move a node to a new parent, keeping its position on the page. */
  reparent(id: NodeId, parentId: NodeId, position?: number) {
    const n = this.doc.must(id);
    const parent = this.doc.must(parentId);
    if (!isContainer(parent.type)) throw new DocumentError(`${parent.type} cannot contain children`);
    if (id === parentId || this.doc.isAncestor(id, parentId)) throw new DocumentError('Cannot move a node into itself');
    this.transact('Move into', () => {
      this.applyOps([{ op: 'set', id, props: { ...this.placementIn(n, parentId), parent: parentId, index: this.indexExcluding(parentId, id, position) } }]);
    });
  }

  private indexExcluding(parentId: NodeId, id: NodeId, position?: number): string {
    const kids = this.doc.children(parentId).filter((k) => k.id !== id);
    const pos = position === undefined || position > kids.length ? kids.length : Math.max(0, position);
    return indexBetween(pos > 0 ? kids[pos - 1].index : null, pos < kids.length ? kids[pos].index : null);
  }

  /** x/y/rotation for node n so it keeps its world placement under a new parent. */
  placementIn(n: NodeData, parentId: NodeId): NodeProps {
    const world = this.doc.worldTransform(n.id);
    const pw = this.doc.worldTransform(parentId);
    const rel = mul(invert(pw), world);
    const center = apply(rel, { x: n.width / 2, y: n.height / 2 });
    let rotation = (Math.atan2(rel[1], rel[0]) * 180) / Math.PI;
    if (Math.abs(rotation) < 1e-6) rotation = 0;
    return { x: center.x - n.width / 2, y: center.y - n.height / 2, rotation: normalizeAngle(rotation) };
  }

  /** Wrap nodes in a new container (GROUP / FRAME / SECTION / COMPONENT). */
  wrap(ids: NodeId[], type: 'GROUP' | 'FRAME' | 'SECTION' | 'COMPONENT', props: NodeProps & { id?: NodeId } = {}): NodeId {
    const nodes = this.topLevel(ids).filter((n) => n.type !== 'PAGE');
    if (!nodes.length) throw new DocumentError('Nothing to wrap');
    const parentId = nodes[0].parent!;
    const sameParent = nodes.every((n) => n.parent === parentId);
    const targetParent = sameParent ? parentId : this.pageId;
    const ordered = [...nodes].sort((a, b) => (a.index < b.index ? -1 : 1));
    let bounds: Rect | null = null;
    for (const n of nodes) bounds = unionRect(bounds, this.doc.worldBounds(n.id));
    const pw = invert(this.doc.worldTransform(targetParent));
    const lb = transformRect(pw, bounds!);
    const id = props.id ?? newId();
    const top = ordered[ordered.length - 1];
    const kids = this.doc.children(targetParent);
    const topPos = kids.findIndex((k) => k.id === top.id);
    const nextIdx = topPos >= 0 && topPos + 1 < kids.length ? kids[topPos + 1].index : null;
    const index = sameParent ? indexBetween(top.index, nextIdx) : this.doc.indexAt(targetParent);
    const base: NodeProps = type === 'GROUP' ? {} : type === 'FRAME' ? { fills: [], clipsContent: false } : {};
    this.transact(`Wrap in ${type.toLowerCase()}`, () => {
      this.applyOps([
        { op: 'create', node: makeNode(type, { id, parent: targetParent, index, x: lb.x, y: lb.y, width: lb.width, height: lb.height, ...base, ...props }) },
      ]);
      const idx = indicesBetween(null, null, ordered.length);
      this.applyOps(ordered.map((n, i) => ({ op: 'set', id: n.id, props: { ...this.placementIn(n, id), parent: id, index: idx[i] } })));
      this.setSelection([id]);
    });
    return id;
  }

  group(ids: NodeId[]): NodeId {
    return this.wrap(ids, 'GROUP');
  }

  /** Dissolve groups/frames, moving children to the parent in place. */
  ungroup(ids: NodeId[]): NodeId[] {
    const freed: NodeId[] = [];
    this.transact('Ungroup', () => {
      for (const id of ids) {
        const g = this.doc.get(id);
        if (!g || !g.parent || !['GROUP', 'FRAME', 'SECTION', 'COMPONENT', 'INSTANCE'].includes(g.type)) continue;
        const kids = this.doc.children(id);
        const siblings = this.doc.children(g.parent);
        const pos = siblings.findIndex((s) => s.id === id);
        const after = pos + 1 < siblings.length ? siblings[pos + 1].index : null;
        const idx = indicesBetween(g.index, after, kids.length);
        this.applyOps(kids.map((k, i) => ({ op: 'set', id: k.id, props: { ...this.placementIn(k, g.parent!), parent: g.parent!, index: idx[i] } })));
        this.applyOps([{ op: 'delete', id }]);
        freed.push(...kids.map((k) => k.id));
      }
      this.setSelection(freed);
    });
    return freed;
  }

  // Z-order --------------------------------------------------------------

  bringToFront(ids: NodeId[]) {
    this.reorder(ids, (kids, sel) => [...kids.filter((k) => !sel.has(k.id)), ...kids.filter((k) => sel.has(k.id))]);
  }
  sendToBack(ids: NodeId[]) {
    this.reorder(ids, (kids, sel) => [...kids.filter((k) => sel.has(k.id)), ...kids.filter((k) => !sel.has(k.id))]);
  }
  bringForward(ids: NodeId[]) {
    this.reorder(ids, (kids, sel) => {
      const a = [...kids];
      for (let i = a.length - 2; i >= 0; i--) if (sel.has(a[i].id) && !sel.has(a[i + 1].id)) [a[i], a[i + 1]] = [a[i + 1], a[i]];
      return a;
    });
  }
  sendBackward(ids: NodeId[]) {
    this.reorder(ids, (kids, sel) => {
      const a = [...kids];
      for (let i = 1; i < a.length; i++) if (sel.has(a[i].id) && !sel.has(a[i - 1].id)) [a[i], a[i - 1]] = [a[i - 1], a[i]];
      return a;
    });
  }

  /** Move a node to an explicit position among its siblings (0 = back). */
  setZIndex(id: NodeId, position: number) {
    const n = this.doc.must(id);
    this.transact('Reorder', () => this.applyOps([{ op: 'set', id, props: { index: this.indexExcluding(n.parent!, id, position) } }]));
  }

  private reorder(ids: NodeId[], fn: (kids: NodeData[], sel: Set<NodeId>) => NodeData[]) {
    const byParent = new Map<NodeId, Set<NodeId>>();
    for (const n of this.topLevel(ids)) {
      if (!n.parent) continue;
      if (!byParent.has(n.parent)) byParent.set(n.parent, new Set());
      byParent.get(n.parent)!.add(n.id);
    }
    this.transact('Reorder', () => {
      for (const [parent, sel] of byParent) {
        const kids = this.doc.children(parent);
        const next = fn(kids, sel);
        // Re-index only the moved nodes, between their new neighbours.
        const ops: Op[] = [];
        const keys = next.map((k) => k.index);
        for (let i = 0; i < next.length; i++) {
          if (!sel.has(next[i].id)) continue;
          const lo = i > 0 ? keys[i - 1] : null;
          let hiIdx = i + 1;
          while (hiIdx < next.length && sel.has(next[hiIdx].id)) hiIdx++;
          const hi = hiIdx < next.length ? keys[hiIdx] : null;
          const k = lo !== null && hi !== null && lo >= hi ? indexBetween(lo, null) : indexBetween(lo, hi);
          keys[i] = k;
          if (k !== next[i].index) ops.push({ op: 'set', id: next[i].id, props: { index: k } });
        }
        this.applyOps(ops);
      }
    });
  }

  // Align & distribute ----------------------------------------------------

  align(ids: NodeId[], mode: 'LEFT' | 'CENTER_H' | 'RIGHT' | 'TOP' | 'CENTER_V' | 'BOTTOM') {
    const nodes = this.topLevel(ids);
    if (!nodes.length) return;
    let target: Rect;
    if (nodes.length === 1) {
      const p = this.doc.get(nodes[0].parent);
      if (!p || p.type === 'PAGE') return;
      target = this.doc.worldBounds(p.id);
    } else {
      target = nodes.map((n) => this.doc.worldBounds(n.id)).reduce<Rect | null>((a, b) => unionRect(a, b), null)!;
    }
    this.transact('Align', () => {
      for (const n of nodes) {
        const b = this.doc.worldBounds(n.id);
        let dx = 0,
          dy = 0;
        if (mode === 'LEFT') dx = target.x - b.x;
        if (mode === 'RIGHT') dx = target.x + target.width - (b.x + b.width);
        if (mode === 'CENTER_H') dx = target.x + target.width / 2 - (b.x + b.width / 2);
        if (mode === 'TOP') dy = target.y - b.y;
        if (mode === 'BOTTOM') dy = target.y + target.height - (b.y + b.height);
        if (mode === 'CENTER_V') dy = target.y + target.height / 2 - (b.y + b.height / 2);
        this.moveBy([n.id], dx, dy);
      }
    });
  }

  distribute(ids: NodeId[], axis: 'H' | 'V', spacing?: number) {
    const nodes = this.topLevel(ids);
    if (nodes.length < 2) return;
    const items = nodes.map((n) => ({ n, b: this.doc.worldBounds(n.id) }));
    items.sort((a, b) => (axis === 'H' ? a.b.x - b.b.x : a.b.y - b.b.y));
    const first = items[0].b;
    const last = items[items.length - 1].b;
    const total = items.reduce((s, i) => s + (axis === 'H' ? i.b.width : i.b.height), 0);
    const span = axis === 'H' ? last.x + last.width - first.x : last.y + last.height - first.y;
    const gap = spacing ?? (span - total) / (items.length - 1);
    this.transact('Distribute', () => {
      let cursor = axis === 'H' ? first.x : first.y;
      for (const it of items) {
        const cur = axis === 'H' ? it.b.x : it.b.y;
        if (axis === 'H') this.moveBy([it.n.id], cursor - cur, 0);
        else this.moveBy([it.n.id], 0, cursor - cur);
        cursor += (axis === 'H' ? it.b.width : it.b.height) + gap;
      }
    });
  }

  /**
   * Tidy up: snap a loose arrangement into a neat row, column or grid with even spacing, keeping
   * the reading order (rows top to bottom, then left to right) and the top-left corner.
   */
  tidyUp(ids: NodeId[], spacing?: number) {
    const nodes = this.topLevel(ids);
    if (nodes.length < 2) return;
    const items = nodes.map((n) => ({ n, b: this.doc.worldBounds(n.id) }));
    // cluster into rows: an item joins a row when its centre is within the row's vertical extent
    items.sort((a, b) => a.b.y + a.b.height / 2 - (b.b.y + b.b.height / 2));
    const rows: (typeof items)[] = [];
    for (const it of items) {
      const row = rows[rows.length - 1];
      const cy = it.b.y + it.b.height / 2;
      if (row && cy <= Math.max(...row.map((r) => r.b.y + r.b.height))) row.push(it);
      else rows.push([it]);
    }
    for (const r of rows) r.sort((a, b) => a.b.x - b.b.x);
    const cols = Math.max(...rows.map((r) => r.length));
    // a single column: every row holds one item
    const column = rows.length > 1 && cols === 1;
    const x0 = Math.min(...items.map((i) => i.b.x)),
      y0 = Math.min(...items.map((i) => i.b.y));
    // spacing: what the user roughly had, rounded; never negative
    const gaps: number[] = [];
    for (const r of rows) for (let i = 1; i < r.length; i++) gaps.push(r[i].b.x - (r[i - 1].b.x + r[i - 1].b.width));
    for (let i = 1; i < rows.length; i++) gaps.push(Math.min(...rows[i].map((x) => x.b.y)) - Math.max(...rows[i - 1].map((x) => x.b.y + x.b.height)));
    const gap = spacing ?? Math.max(0, Math.round(gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 16));
    this.transact('Tidy up', () => {
      if (column) {
        let y = y0;
        for (const it of rows.flat()) {
          this.moveBy([it.n.id], x0 - it.b.x, y - it.b.y);
          y += it.b.height + gap;
        }
        return;
      }
      // grid: uniform cells sized to the largest item, items centred in their cell
      const cw = Math.max(...items.map((i) => i.b.width)),
        ch = Math.max(...items.map((i) => i.b.height));
      const single = rows.length === 1;
      let x = x0;
      rows.forEach((r, ri) =>
        r.forEach((it, ci) => {
          if (single) {
            // a row keeps each item's width (like distribute) and aligns tops
            this.moveBy([it.n.id], x - it.b.x, y0 - it.b.y);
            x += it.b.width + gap;
            return;
          }
          const tx = x0 + ci * (cw + gap) + (cw - it.b.width) / 2;
          const ty = y0 + ri * (ch + gap) + (ch - it.b.height) / 2;
          this.moveBy([it.n.id], tx - it.b.x, ty - it.b.y);
        }),
      );
    });
  }

  // Clipboard & duplication ------------------------------------------------

  copy(ids: NodeId[]): ClipboardData {
    const roots = this.topLevel(ids).filter((n) => n.type !== 'PAGE');
    const nodes: NodeData[] = [];
    const rootWorld: ClipboardData['rootWorld'] = {};
    let bounds: Rect | null = null;
    for (const r of roots) {
      nodes.push(...this.doc.subtree(r.id).map((n) => structuredClone(n)));
      const pl = this.placementIn(r, this.doc.pageOf(r.id)!.id);
      rootWorld[r.id] = { x: pl.x!, y: pl.y!, rotation: pl.rotation! };
      bounds = unionRect(bounds, this.doc.worldBounds(r.id));
    }
    return { format: 'vellum/clipboard', nodes, roots: roots.map((r) => r.id), rootWorld, bounds: bounds ?? { x: 0, y: 0, width: 0, height: 0 } };
  }

  /** Paste clipboard data into parent (default: current page). at = page point for the bounds center, else paste in place. */
  paste(data: ClipboardData, opts: { parent?: NodeId; at?: Vec2; offset?: Vec2 } = {}): NodeId[] {
    const parent = opts.parent ?? this.pageId;
    const map = new Map<NodeId, NodeId>();
    for (const n of data.nodes) map.set(n.id, newId());
    const shift = opts.at ? { x: opts.at.x - rectCenter(data.bounds).x, y: opts.at.y - rectCenter(data.bounds).y } : (opts.offset ?? { x: 0, y: 0 });
    const ops: Op[] = [];
    const rootSet = new Set(data.roots);
    const idx = indicesBetween(this.doc.children(parent).at(-1)?.index ?? null, null, data.roots.length);
    let ri = 0;
    const pw = invert(this.doc.worldTransform(parent));
    for (const n of data.nodes) {
      const copy: NodeData = { ...structuredClone(n), id: map.get(n.id)! };
      if (rootSet.has(n.id)) {
        copy.parent = parent;
        copy.index = idx[ri++];
        const w = data.rootWorld[n.id];
        const c = apply(pw, { x: w.x + n.width / 2 + shift.x, y: w.y + n.height / 2 + shift.y });
        copy.x = c.x - n.width / 2;
        copy.y = c.y - n.height / 2;
        copy.rotation = w.rotation;
      } else {
        copy.parent = map.get(n.parent!) ?? parent;
      }
      if (copy.type === 'CONNECTOR') {
        for (const key of ['connectorStart', 'connectorEnd'] as const) {
          const end = copy[key];
          if (end?.nodeId) {
            if (map.has(end.nodeId)) end.nodeId = map.get(end.nodeId);
            else if (!this.doc.has(end.nodeId)) delete end.nodeId;
          }
          if (end && !end.nodeId) {
            end.x += shift.x;
            end.y += shift.y;
          }
        }
      }
      ops.push({ op: 'create', node: copy });
    }
    const ids = data.roots.map((r) => map.get(r)!);
    this.transact('Paste', () => {
      this.applyOps(ops);
      this.setSelection(ids);
    });
    return ids;
  }

  duplicate(ids: NodeId[], offset: Vec2 = { x: 16, y: 16 }): NodeId[] {
    const roots = this.topLevel(ids);
    if (!roots.length) return [];
    const out: NodeId[] = [];
    this.transact('Duplicate', () => {
      for (const r of roots) {
        const parent = this.doc.get(r.parent);
        const o = isAutoLayout(parent) ? { x: 0, y: 0 } : offset;
        out.push(...this.paste(this.copy([r.id]), { parent: r.parent!, offset: o }));
      }
      this.setSelection(out);
    });
    return out;
  }

  // Components --------------------------------------------------------------

  createComponent(ids: NodeId[]): NodeId {
    const nodes = this.topLevel(ids);
    if (nodes.length === 1 && nodes[0].type === 'FRAME') {
      const id = nodes[0].id;
      this.transact('Create component', () => this.applyOps([{ op: 'set', id, props: { type: 'COMPONENT' } as NodeProps }]));
      return id;
    }
    let bounds: Rect | null = null;
    for (const n of nodes) bounds = unionRect(bounds, this.doc.worldBounds(n.id));
    return this.wrap(ids, 'COMPONENT', { fills: [], name: nodes.length === 1 ? nodes[0].name : 'Component' });
  }

  createInstance(componentId: NodeId, props: NodeProps = {}): NodeId {
    const parent = props.parent ?? this.pageId;
    const { id, ops } = instantiate(this.doc, componentId, parent, this.doc.indexAt(parent), props);
    this.transact('Create instance', () => {
      this.applyOps(ops);
      this.setSelection([id]);
    });
    return id;
  }

  detachInstance(id: NodeId) {
    const n = this.doc.must(id);
    if (n.type !== 'INSTANCE') return;
    this.transact('Detach instance', () => {
      const ops: Op[] = [{ op: 'set', id, props: { type: 'FRAME', componentId: undefined, overrides: undefined } as NodeProps }];
      this.doc.walk(id, (c) => {
        if (c.id !== id && c.sourceId) ops.push({ op: 'set', id: c.id, props: { sourceId: undefined, overrides: undefined } });
        if (c.id !== id && c.type === 'INSTANCE') return false;
      });
      this.applyOps(ops);
    });
  }

  /**
   * Expose a layer inside a component as a property: its visibility (BOOLEAN) or its
   * text (TEXT). Returns the property name.
   */
  /** Instance swap: the components offered first when swapping (Figma's "preferred values"). */
  setPreferredValues(componentId: NodeId, prop: string, values: NodeId[]) {
    const c = this.doc.must(componentId);
    const def = c.componentProps?.[prop];
    if (!def || def.type !== 'INSTANCE_SWAP') throw new DocumentError(`${c.name} has no instance swap property "${prop}"`);
    for (const v of values) if (this.doc.get(v)?.type !== 'COMPONENT') throw new DocumentError(`${v} is not a component`);
    this.update(componentId, { componentProps: { ...c.componentProps, [prop]: { ...def, preferred: values.length ? values : undefined } } }, 'Preferred values');
  }

  addComponentProperty(layerId: NodeId, field: 'visible' | 'characters' | 'componentId', name?: string): string {
    const layer = this.doc.must(layerId);
    const comp = this.doc.ancestors(layerId).find((n) => n.type === 'COMPONENT') ?? (layer.type === 'COMPONENT' ? undefined : undefined);
    if (!comp) throw new DocumentError('The layer is not inside a component');
    const type = field === 'visible' ? 'BOOLEAN' : field === 'characters' ? 'TEXT' : 'INSTANCE_SWAP';
    if (type === 'TEXT' && layer.type !== 'TEXT') throw new DocumentError('Text properties need a text layer');
    if (type === 'INSTANCE_SWAP' && layer.type !== 'INSTANCE') throw new DocumentError('Instance-swap properties need a nested instance');
    const propName = name ?? (field === 'visible' ? `Show ${layer.name}` : layer.name);
    this.transact('Add component property', () => {
      this.applyOps([
        { op: 'set', id: comp.id, props: { componentProps: { ...(comp.componentProps ?? {}), [propName]: { type, default: field === 'visible' ? layer.visible : field === 'characters' ? (layer.characters ?? '') : (layer.componentId ?? '') } } } },
        { op: 'set', id: layerId, props: { propRefs: { ...(layer.propRefs ?? {}), [field]: propName } } },
      ]);
    });
    return propName;
  }

  /** Set property values on instances, e.g. {"Label": "Buy now", "Show icon": false}. */
  setInstanceProperties(instanceId: NodeId, values: Record<string, boolean | string>) {
    const inst = this.doc.must(instanceId);
    if (inst.type !== 'INSTANCE') throw new DocumentError('Not an instance');
    const defs = (inst.componentId && this.doc.get(inst.componentId)?.componentProps) || {};
    for (const k of Object.keys(values)) if (!defs[k]) throw new DocumentError(`Unknown property "${k}". Available: ${Object.keys(defs).join(', ') || 'none'}`);
    // through update, so an instance nested in another keeps its values as an override
    this.update(instanceId, { propValues: { ...(inst.propValues ?? {}), ...values } }, 'Set properties');
  }

  /**
   * Format the paragraphs touched by the character range [start, end) of a text layer, e.g.
   * {list: 'BULLET'} or a function of the current format (for toggles and indenting).
   */
  formatParagraphs(id: NodeId, start: number, end: number, fmt: ParagraphFormat | ((cur: ResolvedParagraph, index: number) => ParagraphFormat)) {
    const n = this.doc.must(id);
    const text = n.characters ?? '';
    const count = text.split('\n').length;
    const a = paragraphAt(text, start);
    const b = paragraphAt(text, Math.max(start, end));
    const cur = paragraphFormats(n) ?? Array.from({ length: count }, () => ({ list: null, level: 0 }));
    const paras: ParagraphFormat[] = Array.from({ length: count }, (_, i) => ({ ...(n.paragraphs?.[i] ?? {}) }));
    // the node-wide list becomes explicit per paragraph so others keep it
    if (n.listStyle) for (let i = 0; i < count; i++) paras[i].list ??= n.listStyle;
    for (let i = a; i <= b; i++) {
      const next = typeof fmt === 'function' ? fmt(cur[i], i) : { ...paras[i], ...fmt };
      paras[i] = { list: next.list ?? undefined, level: next.level || undefined };
      if (!paras[i].list || paras[i].list === 'NONE') delete paras[i].list;
      if (!paras[i].level) delete paras[i].level;
    }
    while (paras.length && !paras[paras.length - 1].list && !paras[paras.length - 1].level) paras.pop();
    this.update(id, { paragraphs: paras.length ? paras : undefined, listStyle: undefined }, 'Format paragraphs');
  }

  /**
   * Turn a shape or vector into text that runs along its outline (the shape is replaced by the
   * text layer, which keeps its position, rotation and path).
   */
  textOnPath(shapeId: NodeId, characters = 'Text on a path', props: NodeProps = {}, at?: Vec2): NodeId {
    const shape = this.doc.must(shapeId);
    const outline = nodeOutline(shape);
    if (!outline?.length || ['TEXT', 'FRAME', 'GROUP', 'SECTION', 'COMPONENT', 'INSTANCE', 'CONNECTOR', 'STICKY'].includes(shape.type)) throw new DocumentError('Text can follow vectors, lines and shapes');
    let id = '';
    this.transact('Text on path', () => {
      id = this.create('TEXT', {
        parent: shape.parent!,
        index: shape.index,
        name: characters.slice(0, 40),
        x: shape.x,
        y: shape.y,
        width: Math.max(1, shape.width),
        height: Math.max(1, shape.height),
        rotation: shape.rotation,
        characters,
        textAutoResize: 'NONE',
        textPath: pathToSvg(outline),
        // start where the user clicked on the path
        ...(at ? { textPathOffset: Math.round(pathDistanceAt(pathToSvg(outline), apply(invert(this.doc.worldTransform(shapeId)), at))) || undefined } : {}),
        ...props,
      });
      this.applyOps([{ op: 'delete', id: shapeId }]);
      this.setSelection([id]);
    });
    return id;
  }

  /** Group the selection and use its bottom-most layer as a mask for the rest. */
  useAsMask(ids: NodeId[]): NodeId {
    const nodes = this.topLevel(ids);
    if (!nodes.length) throw new DocumentError('Nothing to mask');
    let groupId = '';
    this.transact('Use as mask', () => {
      groupId = this.wrap(ids, 'GROUP', { name: 'Mask group' });
      const bottom = this.doc.children(groupId)[0];
      this.applyOps([{ op: 'set', id: bottom.id, props: { isMask: true } }]);
      this.setSelection([groupId]);
    });
    return groupId;
  }

  // Design tokens ----------------------------------------------------------

  createStyle(styleType: 'FILL' | 'TEXT' | 'EFFECT' | 'GRID', name: string, props: NodeProps = {}): NodeId {
    const id = newId();
    this.transact('Create style', () =>
      this.applyOps([{ op: 'create', node: makeNode('STYLE', { id, name, styleType, parent: this.doc.rootId, index: this.doc.indexAt(this.doc.rootId), ...props }) }]),
    );
    return id;
  }

  /** Apply a style to nodes (fill styles can target fills or strokes). */
  applyStyle(ids: NodeId[], styleId: NodeId | null, target: 'fill' | 'stroke' | 'text' | 'effect' | 'grid' = 'fill') {
    const key = ({ fill: 'fillStyleId', stroke: 'strokeStyleId', text: 'textStyleId', effect: 'effectStyleId', grid: 'gridStyleId' } as const)[target];
    this.transact('Apply style', () => this.applyOps(ids.map((id) => ({ op: 'set', id, props: { [key]: styleId ?? undefined } as NodeProps }))));
  }

  createVariableCollection(name: string, modes: string[] = ['Default'], id: NodeId = newId()): NodeId {
    const ms = modes.map((m, i) => ({ id: `m${i + 1}`, name: m }));
    this.transact('Create variable collection', () =>
      this.applyOps([{ op: 'create', node: makeNode('VARIABLE_COLLECTION', { id, name, modes: ms, defaultModeId: ms[0].id, parent: this.doc.rootId, index: this.doc.indexAt(this.doc.rootId) }) }]),
    );
    return id;
  }

  addMode(collectionId: NodeId, name: string): string {
    const c = this.doc.must(collectionId);
    const modes = c.modes ?? [];
    let i = modes.length + 1;
    while (modes.some((m) => m.id === `m${i}`)) i++;
    const mode = { id: `m${i}`, name };
    this.transact('Add mode', () => {
      this.applyOps([{ op: 'set', id: collectionId, props: { modes: [...modes, mode] } }]);
      // new mode starts as a copy of the default mode
      for (const v of this.doc.children(collectionId)) {
        const vals = v.valuesByMode ?? {};
        const base = vals[c.defaultModeId ?? ''] ?? Object.values(vals)[0];
        if (base !== undefined) this.applyOps([{ op: 'set', id: v.id, props: { valuesByMode: { ...vals, [mode.id]: base } } }]);
      }
    });
    return mode.id;
  }

  createVariable(collectionId: NodeId, name: string, resolvedType: VariableType, valuesByMode: Record<string, VariableValue>, id: NodeId = newId()): NodeId {
    this.transact('Create variable', () =>
      this.applyOps([{ op: 'create', node: makeNode('VARIABLE', { id, name, resolvedType, valuesByMode, parent: collectionId, index: this.doc.indexAt(collectionId) }) }]),
    );
    return id;
  }

  /** An extended collection: inherits `baseId`'s variables and modes, and can override values per mode. */
  createExtendedCollection(baseId: NodeId, name: string, id: NodeId = newId()): NodeId {
    const base = this.doc.must(baseId);
    if (base.type !== 'VARIABLE_COLLECTION') throw new DocumentError(`${base.name} is not a variable collection`);
    this.transact('Extend collection', () =>
      this.applyOps([{ op: 'create', node: makeNode('VARIABLE_COLLECTION', { id, name, parent: this.doc.rootId, index: this.doc.indexAt(this.doc.rootId), modes: structuredClone(base.modes ?? []), defaultModeId: base.defaultModeId, extendsCollection: baseId }) }]),
    );
    return id;
  }

  /** Override (or with null, inherit again) a base variable's value in an extended collection. */
  setVariableOverride(collectionId: NodeId, variableId: NodeId, modeId: string, value: VariableValue | null) {
    const c = this.doc.must(collectionId);
    if (!c.extendsCollection) throw new DocumentError(`${c.name} doesn't extend another collection`);
    const all = { ...(c.variableOverrides ?? {}) };
    const forVar = { ...(all[variableId] ?? {}) };
    if (value === null) delete forVar[modeId];
    else forVar[modeId] = value;
    if (Object.keys(forVar).length) all[variableId] = forVar;
    else delete all[variableId];
    this.transact('Override variable', () => this.applyOps([{ op: 'set', id: collectionId, props: { variableOverrides: Object.keys(all).length ? all : undefined } }]));
  }

  setVariableValue(variableId: NodeId, modeId: string, value: VariableValue) {
    const v = this.doc.must(variableId);
    this.transact('Edit variable', () => this.applyOps([{ op: 'set', id: variableId, props: { valuesByMode: { ...(v.valuesByMode ?? {}), [modeId]: value } } }]));
  }

  /** Bind (or unbind with null) a node property to a variable. */
  bindVariable(ids: NodeId[], prop: string, variableId: NodeId | null) {
    if (!(prop in BINDABLE)) throw new DocumentError(`Property "${prop}" can't be bound. Bindable: ${Object.keys(BINDABLE).join(', ')}`);
    if (variableId) {
      const v = this.doc.get(variableId);
      if (!v || v.type !== 'VARIABLE') throw new DocumentError(`Unknown variable ${variableId}`);
      for (const id of ids) {
        const problem = bindProblem(v, this.doc.must(id), prop);
        if (problem) throw new DocumentError(problem);
      }
    }
    this.transact('Bind variable', () =>
      this.applyOps(
        ids.map((id) => {
          const n = this.doc.must(id);
          const b = { ...(n.boundVariables ?? {}) };
          if (variableId) b[prop] = variableId;
          else delete b[prop];
          return { op: 'set', id, props: { boundVariables: Object.keys(b).length ? b : undefined } } as Op;
        }),
      ),
    );
  }

  /** Choose a mode (e.g. Dark) for a collection on a frame/page; descendants inherit it. */
  setVariableMode(id: NodeId, collectionId: NodeId, modeId: string | null) {
    const n = this.doc.must(id);
    const m = { ...(n.variableModes ?? {}) };
    if (modeId) m[collectionId] = modeId;
    else delete m[collectionId];
    this.transact('Set mode', () => this.applyOps([{ op: 'set', id, props: { variableModes: Object.keys(m).length ? m : undefined } }]));
  }

  resetOverrides(id: NodeId) {
    const n = this.doc.must(id);
    this.transact('Reset overrides', () => {
      this.applyOps([{ op: 'set', id, props: { overrides: [] } }]);
      // trigger a sync of the owning component
      const inst = n.type === 'INSTANCE' ? n : this.doc.ancestors(id).find((a) => a.type === 'INSTANCE');
      if (inst?.componentId) this.txChanged.add(inst.componentId);
    });
  }
}

/** True when ops only touch layers they create, and delete every one of them again. */
function addsNothing(ops: Op[]): boolean {
  const created = new Set(ops.flatMap((o) => (o.op === 'create' ? [o.node.id] : [])));
  const deleted = new Set(ops.flatMap((o) => (o.op === 'delete' ? [o.id] : [])));
  return created.size > 0 && ops.every((o) => created.has(o.op === 'create' ? o.node.id : o.id)) && [...created].every((id) => deleted.has(id));
}

function autoName(chars: string): string {
  return chars.replace(/\s+/g, ' ').trim().slice(0, 48);
}

export function normalizeAngle(d: number): number {
  let a = d % 360;
  if (a > 180) a -= 360;
  if (a <= -180) a += 360;
  return Math.round(a * 1000) / 1000;
}

/** Branch colors for mind maps: a soft fill and a stronger line. */
const MIND_BRANCHES = [
  { fill: '#ffe8a3', line: '#f5b800' },
  { fill: '#c6f0d6', line: '#14ae5c' },
  { fill: '#d6e9ff', line: '#0d99ff' },
  { fill: '#f3ddff', line: '#9747ff' },
  { fill: '#ffd9d1', line: '#f24822' },
  { fill: '#cff3f3', line: '#00a3a3' },
];
